import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// ── Global 401 interceptor ─────────────────────────────────────────────
// Root cause of "Firefox dashboard breaks after 10pm": JWTs are 24 h and
// there is no refresh flow, so an admin who logs in at ~10pm sees API
// calls silently 401 the following night. Without an interceptor the UI
// just fails quietly (loading spinners, empty tables) which is worse
// than a hard logout. This registers ONCE at module import so it is
// active from the moment the SPA boots — no reliance on any component
// mount order.
//
// Skips the login endpoint itself so a wrong-password 401 doesn't
// trigger a redirect loop and the login page can render its own error.
let _sessionExpiryToastShown = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const isAuthCall = url.includes('/api/auth/login')
      || url.includes('/api/auth/register');
    if (status === 401 && !isAuthCall && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      // De-duplicate — a page that fires 10 parallel requests would
      // otherwise stack 10 toasts and 10 redirects.
      if (!_sessionExpiryToastShown) {
        _sessionExpiryToastShown = true;
        try {
          toast.error('Your session has expired. Please sign in again.');
        } catch (_) { /* toast may not be mounted */ }
        // Redirect synchronously so this fires BEFORE ProtectedRoute's
        // <Navigate to="/admin/login"> pre-empts us and strips
        // ?expired=1. Testing-agent iter-22 caught the race: the old
        // 400ms setTimeout allowed React to render Navigate first,
        // which then made the interceptor take the reload branch and
        // the operator lost the "session expired" toast on the login
        // page. `.replace` also stops the invalid URL landing in the
        // browser history.
        const onAdmin = window.location.pathname.startsWith('/admin')
          && !window.location.pathname.endsWith('/admin/login');
        // Reset the dedupe flag once the browser starts navigating, so
        // if the user re-logs and the token expires again, we still
        // toast on the second occurrence.
        setTimeout(() => { _sessionExpiryToastShown = false; }, 2000);
        if (onAdmin) {
          window.location.replace('/admin/login?expired=1');
        } else {
          // Non-admin surface (e.g. someone hits an authenticated API
          // from a public page). Just reload to reset in-memory state.
          window.location.reload();
        }
      }
    }
    return Promise.reject(error);
  },
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [token]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = () => ({
    Authorization: `Bearer ${token}`
  });

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, getAuthHeader, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};
